// React imports
import React, { useState, useEffect, useMemo } from 'react';

// Generic imports
import axios from 'axios';
import { XMLParser } from 'fast-xml-parser';
import _ from 'lodash';
import { formatDisplayDateTime } from '../utils/date.js';

// Material UI imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import styled from '@emotion/styled';
import { green } from '@mui/material/colors';

// Local imports
import { accountFromNativeXml, envelopToUpdates } from '../utils/account';
import { useOutletContext } from 'react-router-dom';

function RestOrgUpdates(props) {
    const { accesstoken, onBadge } = useOutletContext();
    const [ loading, setLoading ] = useState(false);
    const [ updates, setUpdates ] = useState(null);
    const [ accounts, setAccounts ] = useState(null);
    const [ suppliers, setSuppliers ] = useState(null);
    const [ working, setWorking ] = useState({});

    useEffect(() => {
        onBadge(<WhiteCircularProgress size={9} />);
        (async () => {
            setLoading(true);
            const [{ data: _updates }, { data: _accounts }, { data: _suppliers }] = await Promise.all([
                axios.get(`/api/xml/getupdatexmls?accesstoken=${accesstoken}`),
                axios.get(`/api/accounts?accesstoken=${accesstoken}&organizationId=0672cfbd-4cb9-4a36-b85d-911807e3f3f8`),
                axios.get(`/api/suppliers?accesstoken=${accesstoken}&organizationId=0672cfbd-4cb9-4a36-b85d-911807e3f3f8`),
            ]);

            const parser = new XMLParser({ ignoreAttributes: false });
            for (let i = 0; i < _updates.length; i++) {
                const u = _updates[i];
                try {
                    const body = parser.parse(u.contents);
                    const xmlBody = body['s:Envelope']['s:Body'];
                    u.updates = await envelopToUpdates({ envelopeBody: xmlBody });
                } catch (e) {
                    console.error('[RestOrgUpdates] error parsing XML', u, e);
                }
            }
            console.log(`[RestOrgUpdates] showing at most 100 updates from ${_updates.length} updates`);
            setUpdates(_.sortBy(_updates, u => parseInt(u.name.split('-')[5].split('.')[0])).slice(0,100));
            setSuppliers(_suppliers.suppliers);
            (async () => {
                let cacheAccounts = [];
                if (_accounts.cache) {
                    cacheAccounts = (await axios(_accounts.cache)).data;
                }
                setAccounts(_.uniqBy([..._accounts.accounts, ...cacheAccounts], 'id'));
            })();
            setLoading(false);
        })();
    }, []);

    useEffect(() => {
        if (updates) {
            const badge = updates.length;
            onBadge(badge > 0 ? badge : null);
        }
    }, [ updates ]);

    if (loading || !updates || !accounts || !suppliers) {
        return <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WhiteCircularProgress size={18} />
        </Box>
    }

    function onView(update) {
        console.log('[RestOrgUpdates] onView', update);
        const win = window.open('', 'Title', 'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes');
        win.document.body.innerText = update.contents + '\n\n' + update.updates.map((u,i) => `UPDATE ${i+1} of ${update.updates.length}\n\n${u.data}`).join('\n\n');
    }

    async function onProcess(update) {
        try {
            console.log('[RestOrgUpdates] onProcess', update);
            setWorking({ ...working, [update.name]: 'working' });
            const { data } = await axios.post(
                '/api/accounts/xml-step2?token=018e9d44-8e80-7814-86d9-576357ef57d6',
                update.contents,
                {
                    headers: {
                        'Content-Type': 'text/xml'
                    }
                }
            );
            console.log('[RestOrgUpdates] onProcess', data);
            const { accounts, suppliers } = data;
            setAccounts(_accounts => _.uniqBy([...accounts, ..._accounts], 'id'));
            setSuppliers(_suppliers => _.uniqBy([...suppliers, ..._suppliers], 'id'));
            setWorking({ ...working, [update.name]: null });
        } catch (e) {
            setWorking({ ...working, [update.name]: 'Error: ' + e.message });
        }
    }

    async function onRemove(update) {
        console.log('[RestOrgUpdates] onRemove', update);
        try {
            setWorking({ ...working, [update.name]: 'working' });
            await axios.delete(`/api/xml/${update.name}?accesstoken=${accesstoken}`);
            setWorking({ ...working, [update.name]: null });
            setUpdates(updates => updates.filter(u => u.name !== update.name));
        } catch (e) {
            setWorking({ ...working, [update.name]: 'Error: ' + e.message });
        }
    }

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            Update Date
                        </TableCell>
                        <TableCell>
                            Company
                        </TableCell>
                        <TableCell>
                            Subject
                        </TableCell>
                        <TableCell>
                            Status
                        </TableCell>
                        <TableCell>
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {updates?.map(u => u.updates?.map((update, i) => (
                        <Row
                            key={update.name}
                            update={update}
                            accounts={accounts}
                            suppliers={suppliers}
                            parent={u}
                            date={new Date(parseInt(u.name.split('-')[5].split('.')[0]))} 
                            index={i} 
                            name={u.name}
                            count={u.updates.length}
                            working={working}
                            onProcess={() => onProcess(u)}
                            onRemove={() => onRemove(u)}
                            onView={() => onView(u)}
                        />
                    ))).flat()}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

function Row({ update, accounts, suppliers, parent, date, index, name, count, working, onProcess, onView, onRemove }) {
    if (update.UniversalInterchange?.Body?.Native?.Body?.Organization) {
        return <RowNative key={update.name} update={update} accounts={accounts} suppliers={suppliers} parent={parent} date={date} index={index} name={name} count={count} working={working} onProcess={onProcess} onView={onView} onRemove={onRemove} />;
    } else if (update.XmlInterchange?.Payload?.Organizations?.Organization) {
        return <RowOrganizations key={update.name} update={update} accounts={accounts} suppliers={suppliers} parent={parent} date={date} index={index} name={name} count={count} working={working} onProcess={onProcess} onView={onView} onRemove={onRemove} />;
    }
    return (
        <TableRow>
            <TableCell>
                {formatDisplayDateTime(date)}
            </TableCell>
            <TableCell>
                Unknown format
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell sx={{ textAlign: 'center' }}>
                <Button variant='outlined' onClick={onView} fullWidth>
                    View
                </Button>
                <Button variant='outlined' color='error' onClick={onRemove} sx={{ mt: 1 }} fullWidth>
                    Remove
                </Button>
            </TableCell>
        </TableRow>
    );
}

function RowOrganizations({ update, date }) {
    return (
        <TableRow>
            <TableCell>
                {formatDisplayDateTime(date)}
            </TableCell>
            <TableCell>
                {update.XmlInterchange?.Payload?.Organizations?.Organization['@_EDICode']}
                &nbsp;/&nbsp;
                {update.XmlInterchange?.Payload?.Organizations?.Organization?.OrganizationDetails?.Name}
            </TableCell>
            <TableCell>
            </TableCell>
        </TableRow>
    )
}

function RowNative({ update, date, accounts, suppliers, index, name, count, working, onProcess, onView, onRemove }) {
    const {account: _account, isAccount, isSupplier} = useMemo(() => {
        return accountFromNativeXml({ xml: update.UniversalInterchange.Body.Native })
    }, [update]);
    const code = _account.uniqueId.split(':')[2];
    const account = useMemo(() => {
        return isAccount ? accounts.find(a => a.uniqueId?.endsWith(`:${code}`)) || accounts.find(a => (a.name || '').toUpperCase() === _account.name.toUpperCase()) : null;
    }, [accounts, _account.name, code, isAccount]);
    const supplier = useMemo(() => {
        return isSupplier ? suppliers.find(s => s.uniqueId?.endsWith(`:${code}`)) || suppliers.find(s => (s.name || '').toUpperCase() === _account.name.toUpperCase()) : null;
    }, [suppliers, _account.name, code, isSupplier]);
    const messageNumber = update.UniversalInterchange.Body.Native.Header.MessageNumberCollection.MessageNumber[0]['#text'];
    const foundAccountUpdate = account?.updates?.find(u => u.html.includes(messageNumber));
    const foundSupplierUpdate = supplier?.updates?.find(u => u.html.includes(messageNumber));
    const taskToDo = (isAccount && !account) || (isSupplier && !supplier) || (isAccount && account && !foundAccountUpdate) || (isSupplier && supplier && !foundSupplierUpdate);

    return (
        <TableRow key={messageNumber}>
            {index === 0 && (
                <TableCell rowSpan={count}>
                    {formatDisplayDateTime(date)}
                </TableCell>
            )}
            <TableCell>
                {_account.uniqueId}
                &nbsp;/&nbsp;
                {_account.name}
                <br/>
                {isAccount && <Chip sx={{ mr: 1, mt: 1 }} label='Account' color='success' />}
                {isSupplier && <Chip sx={{ mr: 1, mt: 1 }} label='Supplier' color='error' />}
                {_account.tags.map(t => <Chip key={t.label} sx={{ mr: 1, mt: 1 }} label={t.label} color='primary' />)}
            </TableCell>
            <TableCell>
                {update.subject}
            </TableCell>
            <TableCell>
                {isAccount && !account && <Box>New Account</Box>}
                {isAccount && account && !foundAccountUpdate && <Box>Account Update</Box>}
                {isAccount && account && foundAccountUpdate && <Box sx={{ color: green[500] }}>Account processed</Box>}
                {isSupplier && !supplier && <Box>New Supplier</Box>}
                {isSupplier && supplier && !foundSupplierUpdate && <Box>Supplier Update</Box>}
                {isSupplier && supplier && foundSupplierUpdate && <Box sx={{ color: green[500] }}>Supplier processed</Box>}
            </TableCell>
            {index === 0 && working[name] !== 'working' && !working[name]?.startsWith('Error:') && (
                <TableCell rowSpan={count} sx={{ textAlign: 'center' }}>
                    <Button variant='outlined' onClick={onView} fullWidth>
                        View
                    </Button>
                    {taskToDo && (
                        <Button variant='outlined' onClick={onProcess} sx={{ mt: 1 }} fullWidth>
                            Create/Process
                        </Button>
                    )}
                    {!taskToDo && (
                        <Button variant='outlined' color='error' onClick={onRemove} sx={{ mt: 1 }} fullWidth>
                            Remove
                        </Button>
                    )}
                </TableCell>
            )}
            {index === 0 && working[name] === 'working' && (
                <TableCell rowSpan={count} sx={{ textAlign: 'center' }}>
                    <CircularProgress size={18} />
                </TableCell>
            )}
            {index === 0 && working[name]?.startsWith('Error:') && (
                <TableCell rowSpan={count} sx={{ textAlign: 'center', color: 'red' }}>
                    <Tooltip title={working[name]}> 
                        <Alert severity='error'>
                            Error
                        </Alert>
                    </Tooltip>
                </TableCell>
            )}
        </TableRow>
    )
}

const WhiteCircularProgress = styled(CircularProgress)({
  color: 'white',
});

export default RestOrgUpdates;